import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, Stethoscope, Building2, ClipboardList, HeartHandshake, CheckCircle2, XCircle, Trash2, RotateCcw, Ban, Pencil } from "lucide-react";

const TABS = [
  { id: "dashboard", l: "Visão geral" },
  { id: "beneficiaries", l: "Beneficiários" },
  { id: "professionals", l: "Profissionais" },
  { id: "clinics", l: "Clínicas" },
  { id: "cross-listed", l: "Cross-list FP↔RAF" },
  { id: "audit", l: "Auditoria" },
  { id: "requests", l: "Solicitações" },
];

const STATUS_LABEL_PT = {
  pending: "Pendentes",
  approved: "Aprovados",
  rejected: "Rejeitados",
  suspended: "Suspensos",
};

const REQUEST_STATUS_PT = {
  solicitada: "Solicitada",
  em_analise: "Em análise",
  aguardando_profissional: "Aguardando profissional",
  confirmada: "Confirmada",
  realizada: "Realizada",
  cancelada: "Cancelada",
  nao_realizada: "Não realizada",
};

const REQUEST_TYPE_PT = {
  consulta: "Consulta",
  exame: "Exame",
  transporte: "Transporte",
  acompanhante: "Acompanhante",
};

export default function AdminFilhoPresente() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-fp">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
        <HeartHandshake className="w-3.5 h-3.5 text-amber-500" /> Filho Presente · Administração
      </div>
      <h1 className="font-display text-3xl font-bold text-[#0B2B62] mb-6">Painel Filho Presente</h1>

      <div className="border-b border-slate-200 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-fp-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? "border-[#0B2B62] text-[#0B2B62]" : "border-transparent text-slate-500"
              }`}>{t.l}</button>
          ))}
        </div>
      </div>

      {tab === "dashboard" && <Dashboard />}
      {tab === "beneficiaries" && <BeneficiariesTab />}
      {tab === "professionals" && <ApprovalTab kind="professional" />}
      {tab === "clinics" && <ApprovalTab kind="clinic" />}
      {tab === "cross-listed" && <CrossListedTab />}
      {tab === "audit" && <AuditWithoutTypeTab />}
      {tab === "requests" && <RequestsTab />}
    </div>
  );
}

const Dashboard = () => {
  const [kpi, setKpi] = useState(null);
  useEffect(() => { api.get("/fp/admin/dashboard").then((r) => setKpi(r.data)); }, []);
  if (!kpi) return <div className="text-slate-500">Carregando…</div>;
  const cards = [
    { l: "Beneficiários ativos", v: kpi.total_beneficiaries, i: Users, c: "bg-amber-50 text-amber-700" },
    { l: "Familiares", v: kpi.total_families, i: Users, c: "bg-blue-50 text-blue-700" },
    { l: "Profissionais aprovados", v: kpi.approved_professionals, i: Stethoscope, c: "bg-emerald-50 text-emerald-700" },
    { l: "Clínicas aprovadas", v: kpi.approved_clinics, i: Building2, c: "bg-emerald-50 text-emerald-700" },
    { l: "Cross-list FP→RAF", v: kpi.cross_fp_to_raf ?? 0, i: HeartHandshake, c: "bg-emerald-50 text-emerald-700",
      hint: "Clínicas FP-origin marcadas como serves_raf → aparecem em /clinicas" },
    { l: "Cross-list RAF→FP", v: kpi.cross_raf_to_fp ?? 0, i: HeartHandshake, c: "bg-amber-50 text-amber-700",
      hint: "Clínicas RAF is_public/serves_fp → aparecem em /filho-presente/clinicas" },
    { l: "Solicitações abertas", v: kpi.open_requests, i: ClipboardList, c: "bg-amber-50 text-amber-700" },
    { l: "Consultas hoje", v: kpi.consultas_today, i: ClipboardList, c: "bg-slate-100 text-slate-700" },
    { l: "Consultas do mês", v: kpi.consultas_month, i: ClipboardList, c: "bg-slate-100 text-slate-700" },
    { l: "Aguardando aprovação", v: kpi.pending_professionals + kpi.pending_clinics, i: ClipboardList, c: "bg-red-50 text-red-700" },
  ];
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.l} className="bg-white border border-slate-200 rounded-xl p-4"
            title={c.hint || ""}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.c} mb-3`}><c.i className="w-4 h-4" /></div>
            <div className="text-2xl font-bold text-[#0B2B62]">{c.v}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.l}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <TopList title="Especialidades mais buscadas" items={kpi.top_specialties} />
        <TopList title="Cidades com mais beneficiários" items={kpi.top_cities} />
      </div>
    </>
  );
};

const TopList = ({ title, items }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">{title}</div>
    {items?.length === 0 ? <div className="text-sm text-slate-500">Sem dados ainda.</div>
      : <ul className="space-y-1.5">
        {items?.map((i) => (
          <li key={i._id || "—"} className="flex justify-between text-sm">
            <span className="truncate">{i._id || "—"}</span>
            <span className="font-semibold text-[#0B2B62]">{i.count}</span>
          </li>
        ))}
      </ul>}
  </div>
);

const BeneficiaryEditModal = ({ open, beneficiary, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !beneficiary) return;
    setForm({
      name: beneficiary.name || "",
      birth_date: beneficiary.birth_date || "",
      phone: beneficiary.phone || "",
      city: beneficiary.city || "",
      state: beneficiary.state || "",
      relation_to_owner: beneficiary.relation_to_owner || "",
      notes: beneficiary.notes || "",
    });
  }, [open, beneficiary]);

  if (!open) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!(form.name || "").trim()) { toast.error("Nome obrigatório"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.patch(`/fp/beneficiaries/${beneficiary.id}`, payload);
      toast.success(`Beneficiário "${form.name}" atualizado.`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fp-benef-edit-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <Pencil className="w-5 h-5" />
            <div className="font-display font-bold text-lg">Editar beneficiário</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Nome *">
            <input value={form.name || ""} onChange={(e) => set("name", e.target.value)}
              data-testid="fp-benef-edit-name"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data de nascimento">
              <input type="date" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)}
                data-testid="fp-benef-edit-birth"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
            <Field label="Relação com o familiar">
              <input value={form.relation_to_owner || ""} onChange={(e) => set("relation_to_owner", e.target.value)}
                placeholder="mãe, pai, avô…"
                data-testid="fp-benef-edit-relation"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
          </div>
          <Field label="Telefone">
            <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
              placeholder="(11) 99999-9999"
              data-testid="fp-benef-edit-phone"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Cidade" className="col-span-2">
              <input value={form.city || ""} onChange={(e) => set("city", e.target.value)}
                data-testid="fp-benef-edit-city"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
            <Field label="UF">
              <input value={form.state || ""} onChange={(e) => set("state", e.target.value.toUpperCase())}
                maxLength={2}
                data-testid="fp-benef-edit-state"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase" />
            </Field>
          </div>
          <Field label="Observações">
            <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
              rows={3} maxLength={500}
              data-testid="fp-benef-edit-notes"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy}
            data-testid="fp-benef-edit-save"
            className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> {busy ? "Salvando…" : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, className = "", children }) => (
  <label className={`block ${className}`}>
    <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const BeneficiariesTab = () => {
  const [list, setList] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api
    .get(`/fp/admin/beneficiaries?include_inactive=${includeInactive}`)
    .then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const onDelete = async (b) => {
    if (!window.confirm(
      `Excluir "${b.name}"?\n\n` +
      "Preserva histórico (marca como inativo). Você pode reativar depois em \"Mostrar excluídos\".",
    )) return;
    try {
      await api.delete(`/fp/admin/beneficiaries/${b.id}`);
      toast.success(`Beneficiário "${b.name}" excluído.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir beneficiário.");
    }
  };

  const onReactivate = async (b) => {
    try {
      await api.post(`/fp/admin/beneficiaries/${b.id}/reactivate`);
      toast.success(`Beneficiário "${b.name}" reativado.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reativar.");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 select-none">
          <input type="checkbox" checked={includeInactive}
            data-testid="fp-admin-beneficiaries-include-inactive"
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded border-slate-300" />
          Mostrar excluídos
        </label>
        <div className="text-xs text-slate-500">{list.length} registro(s)</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Familiar</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Cidade</th>
              <th className="text-left px-4 py-3">Criado</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-xs">Nenhum beneficiário {includeInactive ? "" : "ativo "}cadastrado ainda.</td></tr>
            )}
            {list.map((b) => {
              const inactive = b.active === false;
              return (
                <tr key={b.id} className={`border-t ${inactive ? "bg-slate-50/60 opacity-70" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span>{b.name}</span>
                      {inactive && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">excluído</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{b.owner_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">{b.city}{b.state && `/${b.state}`}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(b.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5 justify-end">
                      {!inactive && (
                        <button onClick={() => setEditing(b)}
                          data-testid={`fp-admin-beneficiary-edit-${b.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </button>
                      )}
                      {inactive ? (
                        <button onClick={() => onReactivate(b)}
                          data-testid={`fp-admin-beneficiary-reactivate-${b.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200 rounded-md hover:bg-emerald-50">
                          <RotateCcw className="w-3.5 h-3.5" /> Reativar
                        </button>
                      ) : (
                        <button onClick={() => onDelete(b)}
                          data-testid={`fp-admin-beneficiary-delete-${b.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" /> Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <BeneficiaryEditModal
        open={!!editing}
        beneficiary={editing}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </div>
  );
};

const ApprovalTab = ({ kind }) => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending");
  const endpoint = kind === "professional" ? "professionals" : "clinics";
  const load = () => api.get(`/fp/admin/${endpoint}?status=${filter || ""}`)
    .then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/fp/admin/${endpoint}/${id}/approval`, { status });
      toast.success("Atualizado");
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const toggleServesFp = async (it) => {
    try {
      const next = !(it.serves_fp_effective ?? it.serves_fp);
      await api.patch(`/fp/admin/${endpoint}/${it.id}/serves-fp`, { serves_fp: next });
      toast.success(next ? "Aparece no FP" : "Removido do FP");
      load();
    } catch { toast.error("Erro ao alternar"); }
  };

  // Iter 68.66.h — Toggle recíproco: clínica FP-origin também aparece na
  // vitrine RAF (`/clinicas`). Endpoint criado no Iter 68.66.f. Só faz
  // sentido para `kind === "clinic"` (profs têm dinâmica diferente).
  const toggleServesRaf = async (it) => {
    if (kind !== "clinic") return;
    try {
      const next = !it.serves_raf;
      await api.patch(`/fp/admin/clinics/${it.id}/serves-raf`, { serves_raf: next });
      toast.success(next ? "Aparece na RAF" : "Removido da RAF");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao alternar RAF"); }
  };

  const remove = async (it) => {
    const label = it.display_name || it.name || "este item";
    if (!window.confirm(`Excluir ${label}?\n\nSe tiver histórico de consultas, ele apenas será desvinculado do Filho Presente (mantido na RAF).`)) return;
    try {
      const r = await api.delete(`/fp/admin/${endpoint}/${it.id}`);
      toast.success(r.data?.message || "Excluído.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };

  const SourceBadge = ({ source }) => {
    if (source === "raf") return <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-900 text-white">RAF</span>;
    if (source === "fp") return <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">FP</span>;
    return <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">FP pendente</span>;
  };

  return (
    <>
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "approved", "rejected", "suspended"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            data-testid={`fp-admin-filter-${s}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              filter === s ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white border-slate-300 text-slate-600"
            }`}>{STATUS_LABEL_PT[s] || s}</button>
        ))}
        <div className="ml-auto text-xs text-slate-500 self-center">
          Base unificada RAF + Filho Presente · {list.length} registro(s)
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y">
        {list.length === 0 ? <div className="p-6 text-slate-500 text-sm">Nada aqui.</div>
          : list.map((it) => {
            const servesFp = it.serves_fp_effective ?? it.serves_fp ?? (it.source !== "raf");
            return (
              <div key={it.id} className="p-4 flex items-start gap-3 flex-wrap" data-testid={`fp-admin-item-${it.id}`}>
                {it.photo_url ? (
                  <img src={it.photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-800 truncate">{it.display_name || it.name}</div>
                    <SourceBadge source={it.source} />
                    {it.certification_tier && (
                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        it.certification_tier === "ouro" ? "bg-amber-400 text-amber-900" :
                        it.certification_tier === "prata" ? "bg-slate-300 text-slate-800" :
                        "bg-orange-700 text-white"
                      }`}>{it.certification_tier}</span>
                    )}
                    {it.certification_is_lifetime && <span className="text-[9px] uppercase font-bold text-emerald-700">vitalício</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {it.email}
                    {kind === "professional"
                      ? ` · ${it.category || (it.categories || []).join(", ") || "—"}`
                      : ` · ${it.establishment_type || "—"}`}
                    {it.city && ` · ${it.city}${it.state ? "/" + it.state : ""}`}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{STATUS_LABEL_PT[it.status] || it.status}</span>
                    <button onClick={() => toggleServesFp(it)}
                      title={servesFp ? "Aparece no FP — clique pra remover" : "Fora do FP — clique pra incluir"}
                      data-testid={`fp-admin-serves-toggle-${it.id}`}
                      className={`text-[10px] uppercase font-bold px-2 py-1 rounded-md border ${
                        servesFp ? "bg-amber-100 text-amber-800 border-amber-300"
                                  : "bg-white text-slate-500 border-slate-300"
                      }`}>
                      {servesFp ? "★ FP on" : "FP off"}
                    </button>
                    {kind === "clinic" && (
                      <button onClick={() => toggleServesRaf(it)}
                        title={it.serves_raf ? "Aparece também na vitrine RAF — clique pra remover" : "Fora da vitrine RAF — clique pra incluir"}
                        data-testid={`fp-admin-serves-raf-toggle-${it.id}`}
                        className={`text-[10px] uppercase font-bold px-2 py-1 rounded-md border ${
                          it.serves_raf ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                        : "bg-white text-slate-500 border-slate-300"
                        }`}>
                        {it.serves_raf ? "✓ RAF on" : "RAF off"}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {it.status !== "approved" && it.source !== "raf" && (
                      <button onClick={() => setStatus(it.id, "approved")}
                        data-testid={`fp-approve-${it.id}`}
                        className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 rounded-md inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Aprovar
                      </button>
                    )}
                    {it.status !== "rejected" && it.source !== "raf" && (
                      <button onClick={() => setStatus(it.id, "rejected")}
                        className="text-xs bg-white hover:bg-red-50 border border-red-300 text-red-600 px-2.5 py-1.5 rounded-md inline-flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Rejeitar
                      </button>
                    )}
                    <button onClick={() => remove(it)}
                      data-testid={`fp-admin-delete-${it.id}`}
                      className="text-xs bg-white hover:bg-red-50 border border-red-400 text-red-700 px-2.5 py-1.5 rounded-md inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Excluir
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

const RequestsTab = () => {
  return _RequestsTab();
};

// Iter 68.66.i — Cross-list FP↔RAF: reciprocidade visual num único painel.
const CrossListedTab = () => {
  const [fpDual, setFpDual] = useState([]);   // FP-origin com serves_raf
  const [rafDual, setRafDual] = useState([]); // RAF-origin com serves_fp
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [fp, raf] = await Promise.all([
        api.get("/fp/admin/clinics?status=approved"),
        api.get("/admin/clinics"),
      ]);
      setFpDual((fp.data || []).filter((c) => c.serves_raf));
      setRafDual((raf.data || []).filter((c) => c.serves_fp || c.is_public));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-slate-500">Carregando…</div>;

  const total = fpDual.length + rafDual.length;
  return (
    <div className="space-y-6" data-testid="admin-fp-cross-listed">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-1">Aparece nas duas vitrines</div>
        <div className="text-2xl font-bold text-emerald-800">
          {total} clínica{total === 1 ? "" : "s"}
        </div>
        <div className="text-xs text-emerald-700 mt-1">
          Loop de reciprocidade RAF Direct ↔ Filho Presente ativo.
        </div>
      </div>

      <section data-testid="cross-fp-to-raf">
        <h2 className="font-display text-lg font-bold text-[#0B2B62] mb-2">
          FP-origin também na RAF <span className="text-slate-400 text-sm">({fpDual.length})</span>
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Clínicas cadastradas via <b>Filho Presente</b> marcadas como <code>serves_raf=true</code> — aparecem em <code>/clinicas</code>.
        </p>
        {fpDual.length === 0 ? (
          <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-lg p-4">
            Nenhuma clínica FP marcada para aparecer na RAF ainda. Vá em "Clínicas" → aba Aprovados → clique em "RAF off" pra ativar.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl divide-y">
            {fpDual.map((c) => (
              <div key={c.id} className="p-3 flex items-center justify-between gap-3"
                data-testid={`cross-fp-raf-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.establishment_type || "—"} · {c.city}{c.state && "/" + c.state}</div>
                </div>
                <div className="flex gap-1">
                  <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">FP</span>
                  <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">RAF</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="cross-raf-to-fp">
        <h2 className="font-display text-lg font-bold text-[#0B2B62] mb-2">
          RAF-origin também no FP <span className="text-slate-400 text-sm">({rafDual.length})</span>
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Clínicas <b>RAF Direct</b> com <code>serves_fp=true</code> OU <code>is_public=true</code> — aparecem em <code>/filho-presente/clinicas</code>.
        </p>
        {rafDual.length === 0 ? (
          <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-lg p-4">
            Nenhuma clínica RAF exposta na vitrine FP ainda.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl divide-y">
            {rafDual.map((c) => (
              <div key={c.id} className="p-3 flex items-center justify-between gap-3"
                data-testid={`cross-raf-fp-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{c.name} {c.code && <span className="text-slate-400 text-xs">· {c.code}</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{c.establishment_type || "Clínica de Reabilitação (default)"} · {c.city}{c.state && "/" + c.state}</div>
                </div>
                <div className="flex gap-1">
                  <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">RAF</span>
                  {(c.serves_fp || c.is_public) && (
                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">FP</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// Iter 68.66.i — Auditoria: clínicas RAF sem establishment_type.
// Backend endpoint = /api/admin/clinics/audit/without-establishment-type
const AuditWithoutTypeTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/clinics/audit/without-establishment-type")
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, items: [] }));
  }, []);
  if (!data) return <div className="text-slate-500">Carregando…</div>;
  return (
    <div className="space-y-4" data-testid="admin-fp-audit-notype">
      <div className={`border rounded-xl p-4 ${data.count === 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <div className={`text-xs uppercase tracking-widest font-bold mb-1 ${data.count === 0 ? "text-emerald-700" : "text-amber-800"}`}>
          Clínicas sem tipo definido
        </div>
        <div className={`text-2xl font-bold ${data.count === 0 ? "text-emerald-800" : "text-amber-900"}`}>
          {data.count}
        </div>
        <div className={`text-xs mt-1 ${data.count === 0 ? "text-emerald-700" : "text-amber-800"}`}>
          {data.count === 0
            ? "Tudo em dia — toda unidade tem tipo próprio."
            : "Estas unidades usam o default \"Clínica de Reabilitação\" na vitrine. Edite pela tabela em Governança → Unidades → coluna Tipo (vitrine)."}
        </div>
      </div>
      {data.count > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Cidade</th>
                <th className="text-left px-4 py-3">E-mail</th>
                <th className="text-right px-4 py-3">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((c) => (
                <tr key={c.id} data-testid={`audit-notype-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{c.code || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{c.city}{c.state && "/" + c.state}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[220px]">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <a href="/admin" data-testid={`audit-notype-open-${c.id}`}
                      className="text-xs text-[#0B2B62] hover:underline font-semibold">
                      Editar em Governança →
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const _RequestsTab = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/fp/admin/requests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const cancelReq = async (r) => {
    if (!window.confirm(`Cancelar solicitação de ${REQUEST_TYPE_PT[r.type] || r.type} para ${r.beneficiary_name}?`)) return;
    try {
      await api.patch(`/fp/requests/${r.id}/status`, { status: "cancelada", note: "Cancelada pelo admin" });
      toast.success("Solicitação cancelada.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao cancelar."); }
  };

  const removeReq = async (r) => {
    if (!window.confirm(`EXCLUIR DEFINITIVAMENTE a solicitação #${r.id.slice(0,8)}?\n\nEssa ação apaga o registro. Use "Cancelar" pra preservar o histórico.`)) return;
    try {
      await api.delete(`/fp/admin/requests/${r.id}`);
      toast.success("Solicitação excluída.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir."); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
          <tr>
            <th className="text-left px-4 py-3">Tipo</th>
            <th className="text-left px-4 py-3">Beneficiário</th>
            <th className="text-left px-4 py-3 hidden sm:table-cell">Detalhe</th>
            <th className="text-left px-4 py-3">Data</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-right px-4 py-3">Ações</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500 text-xs">Sem solicitações ainda.</td></tr>
          )}
          {list.map((r) => {
            const done = ["cancelada", "realizada", "nao_realizada"].includes(r.status);
            return (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3">{REQUEST_TYPE_PT[r.type] || r.type}</td>
                <td className="px-4 py-3">{r.beneficiary_name}</td>
                <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{r.specialty || r.exam_type || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{r.desired_date || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{REQUEST_STATUS_PT[r.status] || r.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {!done && (
                      <button onClick={() => cancelReq(r)}
                        data-testid={`fp-admin-request-cancel-${r.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                        <Ban className="w-3.5 h-3.5" /> Cancelar
                      </button>
                    )}
                    <button onClick={() => removeReq(r)}
                      data-testid={`fp-admin-request-delete-${r.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
